import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { formatRupiah } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { RupiahInput } from "@/components/RupiahInput";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { FileUploader } from "@/components/FileUploader";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Loader2, Car, AlertTriangle, ChevronsUpDown, Check } from "lucide-react";
import { toast } from "sonner";

const JOBS = [
  { key: "kir", label: "KIR" },
  { key: "pajak", label: "Pajak" },
  { key: "oli", label: "Ganti Oli" },
  { key: "servis", label: "Servis" },
];

function recoText(r) {
  if (!r) return null;
  if (r.key === "oli" && (r.status === "perlu_diskusi" || r.status === "unknown" || !r.due_date)) return "Perlu dicek km";
  if (r.status === "overdue") return `Terlambat ${Math.abs(r.days)} hari`;
  if (r.status === "upcoming") return `Jatuh tempo ${r.days} hari lagi`;
  if (r.status === "perlu_diskusi") return "Perlu diskusi";
  return null;
}

function vehicleRecos(v) {
  if (!v?.reminders) return [];
  return JOBS.map((j) => {
    const t = recoText(v.reminders[j.key]);
    return t ? `${j.label} - ${t}` : null;
  }).filter(Boolean);
}

const BranchPicker = ({ branches, value, onChange }) => {
  const [open, setOpen] = useState(false);
  const label = value === "all" ? "Semua Cabang" : branches.find((b) => b.id === value)?.name || "Semua Cabang";
  const pick = (v) => { onChange(v); setOpen(false); };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" className="mt-1.5 w-full justify-between font-normal" data-testid="veh-expense-branch-trigger">
          <span className="truncate">{label}</span><ChevronsUpDown className="h-4 w-4 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[--radix-popover-trigger-width]" align="start">
        <Command>
          <CommandInput placeholder="Cari cabang..." data-testid="veh-expense-branch-search" />
          <CommandList>
            <CommandEmpty>Cabang tidak ditemukan</CommandEmpty>
            <CommandItem value="Semua Cabang" onSelect={() => pick("all")} data-testid="veh-expense-branch-option-all">
              <Check className={`mr-2 h-4 w-4 ${value === "all" ? "opacity-100" : "opacity-0"}`} />Semua Cabang
            </CommandItem>
            {branches.map((b) => (
              <CommandItem key={b.id} value={b.name} onSelect={() => pick(b.id)} data-testid={`veh-expense-branch-option-${b.id}`}>
                <Check className={`mr-2 h-4 w-4 ${value === b.id ? "opacity-100" : "opacity-0"}`} />{b.name}
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

const branchKey = (v) => v.branch_id || v.branch || "-";

export const VehicleExpenseForm = ({ vehicles, user, onDone }) => {
  const [vehicleId, setVehicleId] = useState("");
  const branches = useMemo(() => {
    const m = new Map();
    vehicles.forEach((v) => { const k = branchKey(v); if (!m.has(k)) m.set(k, { id: k, name: v.branch || "Tanpa Cabang" }); });
    return [...m.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [vehicles]);
  const [branchSel, setBranchSel] = useState(null);
  const branchFilter = branchSel ?? (branches.some((b) => b.id === user?.branch_id) ? user.branch_id : "all");
  const shownVehicles = branchFilter === "all" ? vehicles : vehicles.filter((v) => branchKey(v) === branchFilter);
  const [sel, setSel] = useState({});
  const [method, setMethod] = useState({});
  const [cost, setCost] = useState({});
  const [doneSource, setDoneSource] = useState("pusat");
  const [odometer, setOdometer] = useState("");
  const [oliNote, setOliNote] = useState("");
  const [note, setNote] = useState("");
  const [notaFiles, setNotaFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [cashBal, setCashBal] = useState(null);
  const loadBal = () => api.get("/my-balance").then((r) => setCashBal(r.data.available || 0)).catch(() => setCashBal(0));
  useEffect(() => { loadBal(); }, []);

  const vehicle = useMemo(() => vehicles.find((v) => v.id === vehicleId), [vehicles, vehicleId]);
  const recos = vehicle ? vehicleRecos(vehicle) : [];

  const resetVehicle = (v) => { setVehicleId(v); setSel({}); setCost({}); setMethod({}); setOdometer(""); setOliNote(""); };

  const toggle = (k) => {
    setSel((s) => ({ ...s, [k]: !s[k] }));
    setMethod((m) => (m[k] ? m : { ...m, [k]: "cash" }));
    setCost((c) => {
      if (c[k] != null && c[k] !== "") return c;
      const rc = vehicle?.reminders?.[k]?.cost || 0;
      return { ...c, [k]: rc ? String(rc) : "" };
    });
  };

  const chosen = JOBS.filter((j) => sel[j.key]);
  const total = chosen.reduce((s, j) => s + (Number(cost[j.key]) || 0), 0);
  const isCabang = doneSource === "cabang";
  const cashTotal = isCabang ? 0 : chosen.filter((j) => (method[j.key] || "cash") === "cash").reduce((s, j) => s + (Number(cost[j.key]) || 0), 0);

  const resetAll = () => {
    setSel({}); setMethod({}); setCost({}); setOdometer(""); setOliNote(""); setNote("");
    setNotaFiles([]); setVehicleId(""); setDoneSource("pusat");
  };

  const submit = async () => {
    if (!vehicleId) { toast.error("Pilih mobil terlebih dahulu"); return; }
    if (!chosen.length) { toast.error("Pilih minimal satu pekerjaan"); return; }
    for (const j of chosen) {
      if (!Number(cost[j.key]) || Number(cost[j.key]) <= 0) { toast.error(`Isi nominal biaya untuk ${j.label}`); return; }
    }
    if (sel.oli && !Number(odometer)) { toast.error("Masukkan KM kendaraan saat ini untuk Ganti Oli"); return; }
    if (isCabang && !notaFiles.length) { toast.error("Upload nota bukti dari cabang"); return; }
    if (isCabang && !note.trim()) { toast.error("Keterangan wajib diisi untuk jalur Cabang"); return; }
    if (cashTotal > 0 && !notaFiles.length) { toast.error("Upload nota bukti untuk pengeluaran Cash"); return; }
    if (cashTotal > 0) {
      const bal = (await api.get("/my-balance")).data.available || 0;
      setCashBal(bal);
      if (cashTotal > bal + 0.001) { toast.error(`Saldo kas tidak cukup (sisa ${formatRupiah(bal)}). Ajukan kas terlebih dahulu.`); return; }
    }

    setBusy(true);
    const errors = [];
    const doneJobs = [];
    let oliDiscussion = false;

    for (const j of chosen) {
      const payload = {
        item_key: j.key,
        actual_cost: Number(cost[j.key]) || 0,
        note: j.key === "oli" ? (oliNote || note) : note,
        current_odometer: j.key === "oli" ? Number(odometer) : undefined,
        done_source: doneSource,
        payment_method: isCabang ? "" : (method[j.key] || "cash"),
        nota_file_ids: notaFiles.map((f) => f.file_id),
      };
      try {
        await api.post(`/vehicles/${vehicleId}/mark-done`, payload);
        doneJobs.push(j);
      } catch (err) {
        const detail = formatApiErrorDetail(err.response?.data?.detail) || "";
        if (j.key === "oli" && /diskusi|km sejak|belum/i.test(detail)) {
          if (!oliNote.trim()) { errors.push("Ganti Oli: wajib isi keterangan untuk status Perlu Diskusi"); continue; }
          try {
            await api.post(`/vehicles/${vehicleId}/oil/discussion`, { current_odometer: Number(odometer), note: oliNote });
            oliDiscussion = true;
          } catch (e2) { errors.push(`Ganti Oli (diskusi): ${formatApiErrorDetail(e2.response?.data?.detail)}`); }
        } else {
          errors.push(`${j.label}: ${detail}`);
        }
      }
    }

    if (!isCabang) {
      const cashJobs = doneJobs.filter((j) => (method[j.key] || "cash") === "cash");
      const tempoJobs = doneJobs.filter((j) => method[j.key] === "tempo");
      if (tempoJobs.length) {
        const amt = tempoJobs.reduce((s, j) => s + (Number(cost[j.key]) || 0), 0);
        try {
          await api.post("/vehicle-payables", { vehicle_id: vehicleId, items: tempoJobs.map((j) => j.label), amount: amt, note });
        } catch (e) { errors.push(`Tagihan tempo: ${formatApiErrorDetail(e.response?.data?.detail)}`); }
      }
      if (cashJobs.length) {
        const amt = cashJobs.reduce((s, j) => s + (Number(cost[j.key]) || 0), 0);
        const labels = cashJobs.map((j) => j.label).join(", ");
        try {
          await api.post("/general-expenses", {
            file_id: notaFiles[0]?.file_id,
            amount: amt,
            description: `${labels} kendaraan ${vehicle?.plate_number || ""}${note ? ` - ${note}` : ""}`,
            vehicle_id: vehicleId,
            require_balance: true,
          });
        } catch (e) { errors.push(`Potong kas: ${formatApiErrorDetail(e.response?.data?.detail)}`); }
      }
    }

    setBusy(false);
    loadBal();
    onDone?.();
    if (errors.length) { toast.error(errors.join(" | ")); return; }
    const msgs = [];
    if (doneJobs.length) msgs.push(`${doneJobs.length} pekerjaan tercatat selesai & reminder diperbarui`);
    if (oliDiscussion) msgs.push("Ganti Oli diajukan 'Perlu Diskusi' ke Direktur");
    toast.success(msgs.join(". ") || "Pengeluaran tercatat.");
    if (!oliDiscussion || doneJobs.length) resetAll();
  };

  return (
    <div className="space-y-4" data-testid="vehicle-expense-form">
      <div>
        <Label>Cabang</Label>
        <BranchPicker branches={branches} value={branchFilter} onChange={(b) => { setBranchSel(b); resetVehicle(""); }} />
        <div className="h-3" />
        <Label>Mobil / Kendaraan <span className="text-red-500">*</span> <span className="text-xs font-normal text-muted-foreground" data-testid="veh-expense-vehicle-count">({shownVehicles.length} kendaraan)</span></Label>
        <Select value={vehicleId} onValueChange={resetVehicle}>
          <SelectTrigger className="mt-1.5" data-testid="veh-expense-vehicle-select"><SelectValue placeholder="Pilih plat nomor mobil" /></SelectTrigger>
          <SelectContent>
            {shownVehicles.length === 0 ? (
              <div className="px-3 py-2 text-sm text-muted-foreground" data-testid="veh-expense-no-vehicle">Belum ada data kendaraan</div>
            ) : shownVehicles.map((v) => {
              const vr = vehicleRecos(v);
              return (
                <SelectItem key={v.id} value={v.id} data-testid={`veh-expense-vehicle-option-${v.id}`}>
                  <div className="flex flex-col">
                    <span>{v.plate_number} — {v.vehicle_type}{v.branch ? ` (${v.branch})` : ""}</span>
                    {vr.length > 0
                      ? <span className="text-[11px] text-amber-600">{vr.join(" · ")}</span>
                      : <span className="text-[11px] text-emerald-600">Semua aman</span>}
                  </div>
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
        {vehicle && (
          <div className="mt-2 rounded-lg border bg-secondary/50 px-3 py-2" data-testid="veh-reco-panel">
            <p className="text-[11px] font-mono uppercase text-muted-foreground mb-1">Rekomendasi pengerjaan</p>
            {recos.length > 0 ? (
              <ul className="text-sm space-y-0.5">
                {recos.map((t, i) => <li key={i} className="flex items-center gap-1.5 text-amber-700"><AlertTriangle className="h-3.5 w-3.5" />{t}</li>)}
              </ul>
            ) : <p className="text-sm text-emerald-700">Semua jadwal aman.</p>}
          </div>
        )}
      </div>

      <div>
        <Label>Dilakukan Oleh <span className="text-red-500">*</span></Label>
        <div className="mt-1.5 flex gap-2" data-testid="veh-done-source">
          {[["pusat", "Tim Kendaraan Pusat"], ["cabang", "Cabang"]].map(([val, lbl]) => (
            <button key={val} type="button" onClick={() => setDoneSource(val)}
              className={`flex-1 text-sm px-3 py-2 rounded-md border transition-colors ${doneSource === val ? "border-brand bg-brand/10 text-brand font-semibold" : "hover:bg-secondary"}`}
              data-testid={`veh-done-source-${val}`}>{lbl}</button>
          ))}
        </div>
        <p className="text-[11px] text-muted-foreground mt-1">
          {isCabang ? "Cabang mengerjakan & bayar sendiri — wajib upload nota & isi nominal biaya." : "Tim Pusat — pilih metode bayar Cash (potong saldo kas berjalan) atau Tempo (tagihan) per pekerjaan."}
        </p>
      </div>

      <div>
        <Label>Pekerjaan <span className="text-red-500">*</span> <span className="text-xs text-muted-foreground">(boleh pilih lebih dari satu)</span></Label>
        <div className="mt-1.5 space-y-2">
          {JOBS.map((j) => {
            const checked = !!sel[j.key];
            const m = method[j.key] || "cash";
            const jr = vehicle?.reminders?.[j.key];
            return (
              <div key={j.key} className={`rounded-lg border p-3 transition-colors ${checked ? "border-brand bg-brand/5" : ""}`} data-testid={`veh-job-${j.key}`}>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={checked} onChange={() => toggle(j.key)} className="h-4 w-4 accent-emerald-700" data-testid={`veh-job-check-${j.key}`} />
                  <span className="font-medium text-sm">{j.label}</span>
                  {jr && recoText(jr) && <span className="text-[11px] text-amber-600 ml-1">{recoText(jr)}</span>}
                </label>
                {checked && (
                  <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2 pl-6">
                    {!isCabang && (
                      <div>
                        <span className="text-xs text-muted-foreground">Metode Bayar</span>
                        <div className="mt-1 flex gap-1.5">
                          {[["cash", "Cash"], ["tempo", "Tempo"]].map(([val, lbl]) => (
                            <button key={val} type="button" onClick={() => setMethod((mm) => ({ ...mm, [j.key]: val }))}
                              className={`text-xs px-3 py-1.5 rounded-md border transition-colors ${m === val ? "bg-brand text-white border-brand" : "hover:bg-secondary"}`}
                              data-testid={`veh-job-method-${j.key}-${val}`}>{lbl}</button>
                          ))}
                        </div>
                      </div>
                    )}
                    <div>
                      <span className="text-xs text-muted-foreground">Nominal Biaya (Rp)</span>
                      <RupiahInput min="0" value={cost[j.key] ?? ""} onChange={(e) => setCost((c) => ({ ...c, [j.key]: e.target.value }))}
                        className="mt-1 font-mono h-9" placeholder="0" data-testid={`veh-job-cost-${j.key}`} />
                    </div>
                    {j.key === "oli" && (
                      <div className="sm:col-span-2 rounded-md border border-amber-200 bg-amber-50/60 p-2.5 space-y-2">
                        <div>
                          <span className="text-xs text-muted-foreground">KM Kendaraan Saat Ini <span className="text-red-500">*</span></span>
                          <Input type="number" min="0" value={odometer} onChange={(e) => setOdometer(e.target.value)}
                            className="mt-1 font-mono h-9 bg-white" placeholder="cth: 85000" data-testid="veh-job-odometer" />
                        </div>
                        <div>
                          <span className="text-xs text-muted-foreground">Keterangan (untuk Perlu Diskusi bila KM belum capai 5.000 km)</span>
                          <Input value={oliNote} onChange={(e) => setOliNote(e.target.value)}
                            className="mt-1 h-9 bg-white" placeholder="Alasan / kondisi kendaraan..." data-testid="veh-job-oli-note" />
                        </div>
                      </div>
                    )}
                    <p className="text-[11px] text-muted-foreground sm:col-span-2">
                      {isCabang
                        ? "Cabang: langsung dicatat selesai dengan nota di bawah."
                        : m === "cash"
                          ? "Cash: memotong saldo kas berjalan Anda (wajib cukup) + langsung dicatat selesai."
                          : "Tempo: langsung jadi tagihan di dashboard + dicatat selesai."}
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="rounded-lg border p-3 space-y-2" data-testid="veh-cabang-section">
        <Label className="text-sm">{isCabang ? <>Nota Bukti dari Cabang <span className="text-red-500">*</span></> : cashTotal > 0 ? <>Nota Bukti Pengeluaran Cash <span className="text-red-500">*</span></> : "Bukti Pengerjaan (opsional)"}</Label>
        <FileUploader files={notaFiles} onChange={setNotaFiles} testId="veh-cabang-nota-uploader" multiple={false} />
        <p className="text-[11px] text-muted-foreground">Bukti tersimpan di riwayat pengerjaan kendaraan & reminder otomatis diperbarui.</p>
      </div>

      <div className="rounded-lg border-2 border-brand bg-brand/5 p-3 flex items-center justify-between" data-testid="veh-total-bar">
        <span className="text-sm font-medium">Total Biaya</span>
        <span className="text-lg font-bold text-brand font-mono" data-testid="veh-total">{formatRupiah(total)}</span>
      </div>

      {!isCabang && (
        <div className={`rounded-lg border p-3 text-sm space-y-1 ${cashTotal > (cashBal || 0) ? "border-rose-300 bg-rose-50" : "bg-secondary/40"}`} data-testid="veh-cash-balance-panel">
          <div className="flex justify-between"><span>Saldo Kas Berjalan Anda</span><span className="font-mono font-semibold" data-testid="veh-cash-balance">{cashBal == null ? "-" : formatRupiah(cashBal)}</span></div>
          <div className="flex justify-between"><span>Pengeluaran Cash</span><span className="font-mono" data-testid="veh-cash-total">{formatRupiah(cashTotal)}</span></div>
          <div className="flex justify-between font-semibold"><span>Sisa Setelah Pengeluaran</span><span className={`font-mono ${cashTotal > (cashBal || 0) ? "text-rose-600" : "text-emerald-700"}`} data-testid="veh-cash-after">{formatRupiah((cashBal || 0) - cashTotal)}</span></div>
          {cashTotal > (cashBal || 0) && <p className="text-xs text-rose-600" data-testid="veh-cash-insufficient">Saldo kas tidak cukup. Ajukan kas terlebih dahulu sebelum pengeluaran Cash.</p>}
        </div>
      )}

      <div>
        <Label htmlFor="veh-note">Keterangan {isCabang ? <span className="text-red-500">*</span> : "(opsional)"}</Label>
        <Input id="veh-note" value={note} onChange={(e) => setNote(e.target.value)} className="mt-1.5" placeholder="Catatan tambahan..." data-testid="veh-note-input" />
      </div>

      <Button type="button" onClick={submit} disabled={busy} className="bg-brand hover:bg-emerald-800" data-testid="veh-expense-submit">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Car className="h-4 w-4 mr-2" /> Proses Pengeluaran</>}
      </Button>
    </div>
  );
};
